import React, { useEffect, useRef, useState } from 'react';
import { getDeskStays, getPlaceShare, sendToStay } from '../../../api/localIntelligence';
import { buildPlaceCardHtml, openPrint } from '../printSheets';
import { BTN, BTN_PRIMARY, EYEBROW } from '../liStyles';

// Getting a place to a guest. With a folio (their stay's page), the desk picks the guest
// and the place appears in their folio under "Sent by the front desk"; the share is
// recorded against the recommendation. Without one, the place's own page is a QR code to
// scan, a link to copy, or a printed card. Every label is the database's.
const INPUT = 'h-9 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400';

const qrFor = (share, url) => {
  if (!share?.park_url || !url) return null;
  const base = share.park_url.split('/p/')[0];
  if (!url.startsWith(base)) return null;
  return `${base}/qr?u=${encodeURIComponent(url.slice(base.length))}`;
};

const SendToGuest = ({ clientId, place, parkName }) => {
  const [query, setQuery] = useState('');
  const [stays, setStays] = useState({ loading: true, data: null, error: null });
  const [share, setShare] = useState(null);
  const [sending, setSending] = useState(null);
  const [sent, setSent] = useState(null);
  const [copied, setCopied] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    getPlaceShare(clientId, place.entity_id).then(setShare).catch(() => setShare(null));
  }, [clientId, place.entity_id]);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setStays((s) => ({ ...s, loading: true, error: null }));
      getDeskStays(clientId, query.trim())
        .then((data) => setStays({ loading: false, data, error: null }))
        .catch((e) => setStays({ loading: false, data: null, error: e?.message || 'Could not load guests.' }));
    }, query ? 300 : 0);
    return () => clearTimeout(debounce.current);
  }, [clientId, query]);

  const send = async (stay) => {
    setSending(stay.stay_id);
    try {
      const r = await sendToStay(clientId, stay.stay_id, place.entity_id, place.recommendation_id || null);
      setSent({ ...r, stay });
    } catch (e) {
      setSent({ error: e?.message || 'Could not send.', stay });
    } finally {
      setSending(null);
    }
  };

  const copy = async () => {
    if (!share?.place_url) return;
    try {
      await navigator.clipboard.writeText(share.place_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // clipboard blocked by the browser; the link is still shown to read out
    }
  };

  const print = () => openPrint(buildPlaceCardHtml({ parkName, place, qrUrl: share?.qr_url, placeUrl: share?.place_url }));

  const rows = stays.data?.rows || [];
  const guestQr = qrFor(share, sent?.short_url || sent?.link_url);

  return (
    <div className="space-y-8" data-testid="li-send-to-guest">
      <section className="space-y-3">
        <h3 className={EYEBROW}>To a guest&apos;s folio</h3>
        <input
          className={`${INPUT} w-full`}
          placeholder="Search a guest by name or site"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search a guest by name or site"
        />
        {stays.data?.label && <p className="text-sm text-gray-500 dark:text-gray-400">{stays.data.label}</p>}
        {stays.error && <p className="text-sm text-red-600 dark:text-red-400">{stays.error}</p>}
        {rows.length > 0 && (
          <ul className="-mx-4 md:-mx-6 divide-y divide-gray-100 dark:divide-gray-800 border-y border-gray-100 dark:border-gray-800">
            {rows.map((r) => (
              <li key={r.stay_id} className="flex items-center justify-between gap-3 px-4 md:px-6 py-2.5">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {r.name_label}{r.site ? ` · Site ${r.site}` : ''}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{r.when_label} · {r.dates_label}</div>
                </div>
                <button type="button" className={BTN_PRIMARY} disabled={Boolean(sending)} onClick={() => send(r)}>
                  {sending === r.stay_id ? 'Sending…' : 'Send'}
                </button>
              </li>
            ))}
          </ul>
        )}
        {sent && (
          <div className="rounded-lg bg-[#32B1FF]/5 border-l-4 border-[#32B1FF] px-3 py-3 space-y-2" aria-live="polite" data-testid="li-sent">
            <p className={`text-sm ${sent.error ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>{sent.error || sent.sent_label}</p>
            {!sent.error && guestQr && (
              <div className="flex items-center gap-3">
                <img src={guestQr} alt={`Code for ${sent.guest_label}'s folio`} className="w-24 h-24 bg-white rounded" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  If {sent.guest_label} is at the desk, they can scan this to open their folio: {sent.short_url || sent.link_url}
                </p>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h3 className={EYEBROW}>No folio link? Show, copy or print</h3>
        {share?.qr_url ? (
          <div className="flex flex-wrap items-center gap-4">
            <img src={share.qr_url} alt={`Code for ${place.name}`} className="w-32 h-32 bg-white rounded" data-testid="li-place-qr" />
            <div className="space-y-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                The guest scans this for {place.name}: hours, directions, menu and phone.
              </p>
              <div className="flex flex-wrap gap-1.5">
                <button type="button" className={BTN} onClick={print}>Print card</button>
                <button type="button" className={BTN} onClick={copy} aria-live="polite">{copied ? 'Copied' : 'Copy link'}</button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading the code…</p>
        )}
      </section>
    </div>
  );
};

export default SendToGuest;
